import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="heading-1 text-foreground mb-4">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-12">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <div className="prose max-w-none">
            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                TMG Impact ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our CSR intelligence and co-funding platform.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using TMG Impact, you agree to this Privacy Policy. If you do not agree, please do not use our services.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">2. Information We Collect</h2>
              
              <h3 className="heading-4 text-foreground mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Contact information (name, email, phone number, organization)</li>
                <li>Account credentials (username, password)</li>
                <li>Organization details (company/NGO name, role, registration documents)</li>
                <li>CSR project data (budgets, timelines, beneficiary information)</li>
                <li>Payment and billing information</li>
                <li>Communications with us (emails, support tickets, form submissions)</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage data (pages visited, time spent, click patterns)</li>
                <li>Cookies and tracking technologies</li>
                <li>Log files and analytics data</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">2.3 Third-Party Data</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Publicly available CSR data</li>
                <li>NGO compliance databases (80G, 12A, FCRA)</li>
                <li>Government mission alignment data</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use collected information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide and maintain our platform services</li>
                <li>Process transactions and manage funding pools</li>
                <li>Verify compliance and conduct due diligence</li>
                <li>Generate impact reports and analytics</li>
                <li>Communicate with you about your account and projects</li>
                <li>Improve our services through analytics and research</li>
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Detect and prevent fraud, abuse, or security incidents</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">4. Information Sharing and Disclosure</h2>
              
              <h3 className="heading-4 text-foreground mb-3">4.1 With Your Consent</h3>
              <p className="text-muted-foreground mb-4">
                We may share your information with third parties when you explicitly consent or direct us to do so.
              </p>

              <h3 className="heading-4 text-foreground mb-3">4.2 Platform Participants</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Project information shared with potential funders</li>
                <li>Funder details shared with implementing NGOs</li>
                <li>Impact data shared with government partners (with permission)</li>
                <li>Anonymized benchmarking data across platform users</li>
              </ul>

              <h3 className="heading-4 text-foreground mb-3">4.3 Service Providers</h3>
              <p className="text-muted-foreground mb-4">
                We work with trusted third-party service providers for hosting, analytics, payment processing, and customer support. These providers are contractually obligated to protect your data.
              </p>

              <h3 className="heading-4 text-foreground mb-3">4.4 Legal Requirements</h3>
              <p className="text-muted-foreground mb-4">
                We may disclose information when required by law, legal process, or government request, or to protect our rights, property, or safety.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">5. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection</li>
                <li>Incident response and breach notification procedures</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">6. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your information for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements. Project data may be retained for audit and compliance purposes as required by CSR regulations.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">7. Your Rights</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access and obtain a copy of your personal data</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent where processing is based on consent</li>
                <li>Lodge a complaint with relevant data protection authorities</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, contact us at privacy@tmgimpact.in
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">8. Cookies and Tracking</h2>
              <p className="text-muted-foreground mb-4">
                We use cookies and similar technologies to enhance your experience. You can control cookies through your browser settings. Disabling cookies may affect platform functionality.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">9. Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our services are not intended for individuals under 18. We do not knowingly collect information from children. If we become aware of such collection, we will take steps to delete the information.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">10. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy periodically. We will notify you of material changes via email or platform notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="heading-3 text-foreground mb-4">11. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For questions about this Privacy Policy or our data practices, contact:
              </p>
              <div className="card-base p-6">
                <p className="text-foreground font-semibold">TMG Impact</p>
                <p className="text-muted-foreground">Email: privacy@tmgimpact.in</p>
                <p className="text-muted-foreground">Address: Mumbai, India</p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <button 
              onClick={() => navigate('/')}
              className="text-sm font-medium hover:underline"
              style={{ color: 'hsl(30 10% 25%)' }}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
